import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import "../../styles/Notifications.css";

// The bell in the nav bar.
//
// It exists because there was nowhere at all that told a host a booking was
// waiting. The fleet page had a "Waiting on you" banner, but you had to
// already be on the fleet page to see it -- so the answer to "is anything
// waiting?" was "go and look", which is not an answer.
//
// --- what it shows, and what it does not -----------------------------------
//
// The server decides. GET /api/notifications derives the whole feed from the
// bookings themselves (utils/notify.py), so this component renders what it is
// given and never works anything out for itself. In particular it does not
// filter by role: one account can be both a host and an advertiser, and the
// server returns whichever sides that account actually has.
//
// TWO NUMBERS, deliberately kept apart:
//   * `unread`      -- is there anything NEW? Drives the badge, cleared by
//                      opening the panel.
//   * `needs_action` -- is there anything I have to DO? Not cleared by
//                      looking, because looking does not answer a booking.
// When the badge is clear but work remains, the panel still says so at the
// foot. A bell that went quiet while three bookings sat unanswered would be
// worse than no bell.

const POLL_MS = 60000;

function BellIcon() {
    return (
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path
                d="M10 2.5a4.6 4.6 0 0 0-4.6 4.6c0 3.2-1.1 4.3-1.6 4.9-.2.2 0 .6.3.6h11.8c.3 0 .5-.4.3-.6-.5-.6-1.6-1.7-1.6-4.9A4.6 4.6 0 0 0 10 2.5Z"
                fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
            />
            <path d="M8.2 15.4a1.9 1.9 0 0 0 3.6 0" fill="none"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
    );
}

// Naive UTC from the API, like every other datetime this backend returns, so
// the Z has to be appended before Date parses it as UTC rather than as local.
// This is the bug that would be invisible: times wrong by the viewer's offset,
// with nothing looking broken. Same rule as campaignFormat.js.
function ago(iso) {
    if (!iso) return "";
    const then = new Date(iso.endsWith("Z") ? iso : iso + "Z");
    if (isNaN(then)) return "";
    const seconds = Math.round((Date.now() - then.getTime()) / 1000);
    if (seconds < 90) return "just now";
    if (seconds < 5400) return `${Math.round(seconds / 60)} min ago`;
    if (seconds < 172800) return `${Math.round(seconds / 3600)} hours ago`;
    return then.toLocaleDateString();
}

export default function NotificationBell({ className = "" }) {
    const [feed, setFeed] = useState(null);
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    const load = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            setFeed(await res.json());
        } catch {
            // The bell is furniture on every page. A failed poll leaves the
            // last good feed on screen rather than blanking the nav bar.
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    // Click-away and Escape both close it.
    useEffect(() => {
        if (!open) return undefined;
        function onDown(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        }
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    async function toggle() {
        const next = !open;
        setOpen(next);
        if (!next || !(feed && feed.unread)) return;
        // Opening IS reading. Marking seen on the server returns the fresh
        // feed, so the items stay listed while the badge clears -- the two are
        // different questions and the response keeps both answers.
        const token = localStorage.getItem("token");
        try {
            const res = await fetch(`${API_BASE_URL}/api/notifications/seen`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) setFeed(await res.json());
        } catch {
            // Failing to mark read is harmless: the badge reappears next poll.
        }
    }

    const unread = (feed && feed.unread) || 0;
    const needsAction = (feed && feed.needs_action) || 0;
    const items = (feed && feed.items) || [];

    return (
        <div className={"nb " + className} ref={boxRef}>
            <button
                type="button"
                className={"nb__btn" + (unread ? " has-unread" : "")}
                onClick={toggle}
                aria-expanded={open}
                aria-haspopup="true"
                aria-label={
                    unread
                        ? `Notifications, ${unread} unread`
                        : "Notifications"
                }
            >
                <BellIcon />
                {unread > 0 && (
                    // aria-hidden: the count is already in the button's label,
                    // and reading it twice is noise.
                    <span className="nb__dot" aria-hidden="true">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div className="nb__panel" role="dialog" aria-label="Notifications">
                    <div className="nb__head">
                        <span>Notifications</span>
                        {needsAction > 0 && (
                            <span className="nb__pill">{needsAction} to answer</span>
                        )}
                    </div>

                    {items.length === 0 ? (
                        <p className="nb__empty">Nothing needs you right now.</p>
                    ) : (
                        <ul className="nb__list">
                            {items.slice(0, 8).map((item, i) => (
                                <li
                                    key={`${item.kind}-${item.request_id}-${i}`}
                                    className={
                                        "nb__item"
                                        + (item.needs_action ? " is-action" : "")
                                        + (item.unread ? " is-unread" : "")
                                    }
                                >
                                    <Link to={item.link} onClick={() => setOpen(false)}>
                                        <span className="nb__item-title">{item.title}</span>
                                        <span className="nb__item-detail">{item.detail}</span>
                                        <span className="nb__item-when">{ago(item.at)}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Said rather than implied. The badge clears on opening,
                        so without this line a host who glanced at the bell has
                        no standing signal that three bookings are unanswered. */}
                    {needsAction > 0 && (
                        <p className="nb__foot">
                            {needsAction === 1
                                ? "One thing is still waiting for you."
                                : `${needsAction} things are still waiting for you.`}
                        </p>
                    )}
                    {items.length > 8 && (
                        <p className="nb__foot">
                            Showing 8 of {items.length}.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
